import { ref, computed, watch, onUnmounted, nextTick } from 'vue';
import Gun from 'gun';
import 'gun/sea';
import { getTalkFlowCore } from '@/composables/TalkFlowCore';
import { toastController } from '@ionic/vue';
import dayjs from 'dayjs';
import router from '@/router';
import { storageServ } from '@/services/globalServices';
import { v4 as uuidv4 } from 'uuid';

interface GroupChatKeyPair {
  pub: string;
  priv: string;
  epub: string;
  epriv: string;
  alias?: string;
}

interface GroupChatGroup {
  name: string;
  pub: string;
  pair: GroupChatKeyPair;
}

export interface GroupChatMessage {
  id: number; // Switched to SQLite's auto-incrementing ID
  msg_id: string; // Gun's message ID
  sender_pub: string;
  sender_alias: string;
  content: string;
  content_type: 'text' | 'leave_notification';
  timestamp: number;
  status: 'sent' | 'pending' | 'failed';
  formattedTime: string;
}

interface GroupChatMember {
  member_pub: string;
  alias: string;
  joined_at: number;
  isOnline: boolean; // This remains a real-time state, not persisted
}

interface GroupChatVote {
  voterId: string;
  vote: 'agree' | 'disagree';
  timestamp: number;
}

interface GroupChatClearSignal {
  initiator: string;
  timestamp: number;
  cleared: boolean;
}

interface GroupChatSession {
  groupPub: string;
  groupName: string;
  isRead: boolean;
  lastActivity: number;
  previewMessage?: GroupChatMessage;
}

// 🆕 环境检测函数
function detectPlatform(): 'ios' | 'android' | 'browser' {
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  
  // 检测iOS
  if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) {
    return 'ios';
  }
  
  // 检测Android  
  if (/android/i.test(userAgent)) {
    return 'android';
  }
  
  // 其他情况视为浏览器
  return 'browser';
}

// 🆕 智能存储类 - 根据平台选择存储方式
class SmartGroupStorage {
  private platform: 'ios' | 'android' | 'browser';
  private useLocalStorage: boolean;
  
  constructor() {
    this.platform = detectPlatform();
    // iOS/Android 优先使用SQLite，浏览器使用localStorage
    this.useLocalStorage = this.platform === 'browser';
    
   // console.log('🔍 检测到平台:', this.platform);
    //console.log('📱 存储策略:', this.useLocalStorage ? 'localStorage' : 'SQLite');
  }
  
  async saveGroups(groups: GroupChatGroup[]): Promise<void> {
   // console.log('💾 开始保存群组...');
    //console.log('📊 保存群组数量:', groups.length);
    
    if (this.useLocalStorage) {
      return this.saveGroupsToLocalStorage(groups);
    } else {
      return this.saveGroupsToSQLite(groups);
    }
  }
  
  async loadGroups(): Promise<GroupChatGroup[]> {
   // console.log('📂 开始加载群组...');
    
    if (this.useLocalStorage) {
      return this.loadGroupsFromLocalStorage();
    } else {
      return this.loadGroupsFromSQLite();
    }
  }
  
  async saveSessions(sessions: GroupChatSession[]): Promise<void> {
  //  console.log('💾 开始保存会话...');
   // console.log('📊 保存会话数量:', sessions.length);
    
    if (this.useLocalStorage) {
      return this.saveSessionsToLocalStorage(sessions);
    } else {
      return this.saveSessionsToSQLite(sessions);
    }
  }
  
  async loadSessions(): Promise<GroupChatSession[]> {
   // console.log('📂 开始加载会话...');
    
    if (this.useLocalStorage) {
      return this.loadSessionsFromLocalStorage();
    } else {
      return this.loadSessionsFromSQLite();
    }
  }
  
  // localStorage实现（浏览器）
  private saveGroupsToLocalStorage(groups: GroupChatGroup[]): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        // 数据压缩：只保留必要字段
        const compactGroups = groups.map(group => ({
          name: group.name,
          pub: group.pub,
          pair: {
            pub: group.pair.pub,
            priv: group.pair.priv,
            epub: group.pair.epub,
            epriv: group.pair.epriv
          }
        }));
        
        const data = {
          groups: compactGroups,
          timestamp: Date.now(),
          version: '1.0'
        };
        
        localStorage.setItem('talkflow_group_chats', JSON.stringify(data));
      //  console.log('✅ localStorage保存成功');
        resolve();
      } catch (error) {
        console.error('❌ localStorage保存失败:', error);
        reject(error);
      }
    });
  }
  
  private loadGroupsFromLocalStorage(): Promise<GroupChatGroup[]> {
    return new Promise((resolve) => {
      try {
        const data = localStorage.getItem('talkflow_group_chats');
        if (!data) {
        //  console.log('📝 localStorage中没有群组数据');
          resolve([]);
          return;
        }
        
        const parsed = JSON.parse(data);
        const groups = parsed.groups || [];
       // console.log('✅ localStorage加载成功，群组数量:', groups.length);
        resolve(groups);
      } catch (error) {
       // console.error('❌ localStorage加载失败:', error);
        resolve([]);
      }
    });
  }
  
  private saveSessionsToLocalStorage(sessions: GroupChatSession[]): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const data = {
          sessions: sessions,
          timestamp: Date.now(),
          version: '1.0'
        };
        
        localStorage.setItem('talkflow_group_sessions', JSON.stringify(data));
       // console.log('✅ localStorage会话保存成功');
        resolve();
      } catch (error) {
        console.error('❌ localStorage会话保存失败:', error);
        reject(error);
      }
    });
  }
  
  private loadSessionsFromLocalStorage(): Promise<GroupChatSession[]> {
    return new Promise((resolve) => {
      try {
        const data = localStorage.getItem('talkflow_group_sessions');
        if (!data) {
         // console.log('📝 localStorage中没有会话数据');
          resolve([]);
          return;
        }
        
        const parsed = JSON.parse(data);
        const sessions = parsed.sessions || [];
       // console.log('✅ localStorage会话加载成功，会话数量:', sessions.length);
        resolve(sessions);
      } catch (error) {
        console.error('❌ localStorage会话加载失败:', error);
        resolve([]);
      }
    });
  }
  
  // SQLite实现（iOS/Android）
  private async saveGroupsToSQLite(groups: GroupChatGroup[]): Promise<void> {
    try {
      // 检查SQLite是否可用
      if (typeof (window as any).SQLitePlugin === 'undefined') {
        console.warn('⚠️ SQLite不可用，降级到localStorage');
        return this.saveGroupsToLocalStorage(groups);
      }
      
      // TODO: 实现SQLite保存逻辑
      console.log('🔧 SQLite群组保存功能开发中，使用localStorage降级');
      return this.saveGroupsToLocalStorage(groups);
    } catch (error) {
      console.error('❌ SQLite保存失败，降级到localStorage:', error);
      return this.saveGroupsToLocalStorage(groups);
    }
  }
  
  private async loadGroupsFromSQLite(): Promise<GroupChatGroup[]> {
    try {
      // 检查SQLite是否可用
      if (typeof (window as any).SQLitePlugin === 'undefined') {
        console.warn('⚠️ SQLite不可用，降级到localStorage');
        return this.loadGroupsFromLocalStorage();
      }
      
      // TODO: 实现SQLite加载逻辑
      console.log('🔧 SQLite群组加载功能开发中，使用localStorage降级');
      return this.loadGroupsFromLocalStorage();
    } catch (error) {
      console.error('❌ SQLite加载失败，降级到localStorage:', error);
      return this.loadGroupsFromLocalStorage();
    }
  }
  
  private async saveSessionsToSQLite(sessions: GroupChatSession[]): Promise<void> {
    try {
      // 检查SQLite是否可用
      if (typeof (window as any).SQLitePlugin === 'undefined') {
        console.warn('⚠️ SQLite不可用，降级到localStorage');
        return this.saveSessionsToLocalStorage(sessions);
      }
      
      // TODO: 实现SQLite会话保存逻辑
      console.log('🔧 SQLite会话保存功能开发中，使用localStorage降级');
      return this.saveSessionsToLocalStorage(sessions);
    } catch (error) {
      console.error('❌ SQLite会话保存失败，降级到localStorage:', error);
      return this.saveSessionsToLocalStorage(sessions);
    }
  }
  
  private async loadSessionsFromSQLite(): Promise<GroupChatSession[]> {
    try {
      // 检查SQLite是否可用
      if (typeof (window as any).SQLitePlugin === 'undefined') {
        console.warn('⚠️ SQLite不可用，降级到localStorage');
        return this.loadSessionsFromLocalStorage();
      }
      
      // TODO: 实现SQLite会话加载逻辑
      console.log('🔧 SQLite会话加载功能开发中，使用localStorage降级');
      return this.loadSessionsFromLocalStorage();
    } catch (error) {
      console.error('❌ SQLite会话加载失败，降级到localStorage:', error);
      return this.loadSessionsFromLocalStorage();
    }
  }
}

// 创建存储实例
const groupStorage = new SmartGroupStorage();

const storageService = storageServ;

// State moved outside the composable function
const newGroupName = ref('');
const joinGroupKey = ref('');
const tempKeyPair = ref<GroupChatKeyPair | null>(null);

const groups = ref<GroupChatGroup[]>([]);
const currentGroup = ref<string | null>(null);
const currentGroupName = ref('');
const messagesByGroup = ref<{ [groupPub: string]: GroupChatMessage[] }>({});
const membersByGroup = ref<{ [groupPub: string]: GroupChatMember[] }>({});
const oldestMessageId = ref<{ [groupPub: string]: number | undefined }>({});
const votesByGroup = ref<{ [groupPub: string]: GroupChatVote[] }>({});
const groupNameListeners: { [groupPub: string]: any } = {};
const groupPreviews = ref<{ [groupPub: string]: { last_msg: string; last_time: number } }>({});
const readTimestamps = ref<{ [groupPub: string]: number }>({});

const isInitialGroupMessageSyncing = ref(false);
let messageActivityTimer: NodeJS.Timeout | null = null;
let lastMessageReceivedTime = 0;

const messageListeners: { [groupPub: string]: any } = {};
const memberListeners: { [groupPub: string]: any } = {};
const voteListeners: { [groupPub: string]: any } = {};
let activityInterval: NodeJS.Timeout | null = null;

export const useGroupChat = () => {
  const { currentUserPub, currentUserAlias, gun, triggerLightHaptic, getAliasRealtime } = getTalkFlowCore();

  const safeUserPub = computed(() => currentUserPub.value || 'No Name');
  const safeUserAlias = computed(() => currentUserAlias.value || 'No Name');

  const agreeCount = computed(() => {
    if (!currentGroup.value) return 0;
    return (votesByGroup.value[currentGroup.value] || []).filter(v => v.vote === 'agree').length;
  });

  const canClearChat = computed(() => {
      if (!currentGroup.value) return false;
      const members = membersByGroup.value[currentGroup.value] || [];
      if (members.length === 0) return false;
      return agreeCount.value === members.length;
  });

  const groupSessions = computed(() => {
    return groups.value.map(group => {
        const preview = groupPreviews.value[group.pub];
        const lastReadTime = readTimestamps.value[group.pub] || 0;
        const hasNewMessages = preview && preview.last_time > lastReadTime;
        
        return {
            groupPub: group.pub,
            groupName: group.name,
            isRead: !hasNewMessages,
            lastActivity: preview?.last_time || 0,
            previewMessage: preview ? {
                content: preview.last_msg,
                formattedTime: formatMessageTime(preview.last_time)
            } : undefined,
        };
    }).sort((a, b) => b.lastActivity - a.lastActivity);
  });

  const formatMessageTime = (timestamp: number): string => {
    return dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss');
  };

  const markGroupAsRead = (groupPub: string) => {
    const preview = groupPreviews.value[groupPub];
    if (preview) {
      readTimestamps.value[groupPub] = preview.last_time;
      // Save to localStorage for persistence
      localStorage.setItem(`group_read_${groupPub}`, preview.last_time.toString());
    }
  };

  const loadReadTimestamps = () => {
    // Load read timestamps from localStorage
    groups.value.forEach(group => {
      const savedTimestamp = localStorage.getItem(`group_read_${group.pub}`);
      if (savedTimestamp) {
        readTimestamps.value[group.pub] = parseInt(savedTimestamp);
      }
    });
  };

  const loadGroupsFromDB = async () => {
    try {
      const dbGroups = await storageService.getAllGroups();
      groups.value = dbGroups.map(g => ({
        pub: g.group_pub,
        name: g.name,
        pair: JSON.parse(g.key_pair_json),
      }));
      
      // Load all group previews
      const previews = await storageService.getAllGroupPreviews();
      groupPreviews.value = {};
      previews.forEach(p => {
        groupPreviews.value[p.group_pub] = {
          last_msg: p.last_msg,
          last_time: p.last_time
        };
      });
      
      // Load read timestamps
      loadReadTimestamps();
      
      // After loading groups, set up listeners for each
      groups.value.forEach(group => {
        listenToGroupMembers(group.pub);
        listenToGroupMessages(group.pub);
        listenToGroupNameChanges(group.pub);
        listenToClearVotes(group.pub); // Add this line
      });
    } catch (error) {
      console.error('Failed to load groups from DB:', error);
      groups.value = [];
    }
  };
  
  const createGroup = async () => {
    const groupNameTrimmed = newGroupName.value.trim();
    if (!groupNameTrimmed) throw new Error('Group name cannot be empty.');

    try {
      const pair = await Gun.SEA.pair() as GroupChatKeyPair;
      if (!pair || !pair.pub || !pair.priv) {
        throw new Error('Key pair generation failed');
      }

      const newGroup: GroupChatGroup = { name: groupNameTrimmed, pub: pair.pub, pair };
      const groupToSave = {
        group_pub: pair.pub,
        name: groupNameTrimmed,
        key_pair_json: JSON.stringify(pair),
      };

      await storageService.saveGroup(groupToSave);
      
      const selfMember = {
          group_pub: pair.pub,
          member_pub: safeUserPub.value,
          alias: safeUserAlias.value,
          joined_at: Date.now()
      };
      
      await storageService.saveGroupMember(selfMember);
      gun.get(`group_${pair.pub}_members`).get(safeUserPub.value).put({ alias: safeUserAlias.value, joinedAt: Date.now() });
      await saveGroupNameToNetwork(pair.pub, groupNameTrimmed);

      groups.value.push(newGroup);
      tempKeyPair.value = pair;
      listenToGroupMembers(pair.pub);
      listenToGroupMessages(pair.pub);
      listenToGroupNameChanges(pair.pub);
      listenToClearVotes(pair.pub);

      newGroupName.value = '';
      return newGroup;
    } catch (error) {
      console.error('Failed to create group:', error);
      tempKeyPair.value = null;
      throw error;
    }
  };
  
  const joinGroupWithKeyPair = async (keyPairString: string) => {
      const pair: GroupChatKeyPair = JSON.parse(keyPairString);
      if (!pair.pub || !pair.priv || !pair.epub || !pair.epriv) {
        throw new Error('Invalid key pair format');
      }

      const groupNameData: any = await new Promise(resolve => gun.get(`group_name_${pair.pub}`).once(data => resolve(data)));
      const groupName = groupNameData?.name || `Group_${pair.pub.slice(0, 8)}`;
      
      if (groups.value.some(g => g.pub === pair.pub)) {
          console.warn('Already in group, skipping join.');
          return; // Return undefined if already in group
      }

      isInitialGroupMessageSyncing.value = true;

      const groupToSave = { group_pub: pair.pub, name: groupName, key_pair_json: JSON.stringify(pair) };
      await storageService.saveGroup(groupToSave);
      
      const selfMember = {
          group_pub: pair.pub,
          member_pub: safeUserPub.value,
          alias: safeUserAlias.value,
          joined_at: Date.now()
      };
      await storageService.saveGroupMember(selfMember);
      gun.get(`group_${pair.pub}_members`).get(safeUserPub.value).put({ alias: safeUserAlias.value, joinedAt: Date.now() });
      
      const newGroup: GroupChatGroup = { name: groupName, pub: pair.pub, pair };
      groups.value.push(newGroup);
      listenToGroupMembers(pair.pub);
      listenToGroupMessages(pair.pub);
      listenToGroupNameChanges(pair.pub);
      listenToClearVotes(pair.pub);

      startMessageSyncMonitor(pair.pub);

      return newGroup; // Return the new group object on success
  };

  const joinGroup = async () => {
    if (!joinGroupKey.value.trim()) {
        throw new Error('Group key is required.');
    }
    await joinGroupWithKeyPair(joinGroupKey.value);
    joinGroupKey.value = '';
  };
  
  const deleteGroup = async (groupPub: string) => {
      // 1. Send leave notification
      const leaveMessage = {
          msg_id: uuidv4(),
          group_pub: groupPub,
          sender_pub: safeUserPub.value,
          sender_alias: safeUserAlias.value,
          content: `${safeUserAlias.value} has left the group.`,
          content_type: 'leave_notification',
          timestamp: Date.now(),
          status: 'pending'
      };
      const dbMessage = await storageService.insertGroupMessage(leaveMessage);
      messagesByGroup.value[groupPub]?.push({ ...dbMessage, formattedTime: formatMessageTime(dbMessage.timestamp) });
      const gunMessage = {
          ...leaveMessage,
          id: leaveMessage.msg_id
      };
      await gun.get(`group_${groupPub}`).set(gunMessage);
      
      // 2. Remove self from Gun
      gun.get(`group_${groupPub}_members`).get(safeUserPub.value).put(null);
      
      // 3. Delete local data
      await storageService.deleteGroup(groupPub);
      
      // Delete preview
      await storageService.deleteGroupPreview(groupPub);
      delete groupPreviews.value[groupPub];
      
      // 4. Update local state
      groups.value = groups.value.filter(g => g.pub !== groupPub);
      delete messagesByGroup.value[groupPub];
      delete membersByGroup.value[groupPub];
      if (groupNameListeners[groupPub]) {
        groupNameListeners[groupPub].off();
        delete groupNameListeners[groupPub];
      }
      if (currentGroup.value === groupPub) {
          currentGroup.value = null;
          currentGroupName.value = '';
      }
  };

  const listenToGroupMembers = (groupPub: string) => {
    if (memberListeners[groupPub]) memberListeners[groupPub].off();
    
    memberListeners[groupPub] = gun.get(`group_${groupPub}_members`).map().on(async (data, key) => {
        console.log(`[Member Listener] Group: ${groupPub}, Key: ${key}, Data:`, data);
        
        let updatedMembers = [...(membersByGroup.value[groupPub] || [])]; // Create a new array for reactivity

        if (!data) {
            // Member left (null data)
            await storageService.deleteGroupMember(groupPub, key);
            const existingIndex = updatedMembers.findIndex(m => m.member_pub === key);
            if (existingIndex > -1) {
                updatedMembers.splice(existingIndex, 1);
                console.log(`[Member Listener] Member ${key} left. Current members:`, updatedMembers);
            }
        } else {
            // Member joined or updated
            const member = {
                group_pub: groupPub,
                member_pub: key,
                alias: data.alias,
                joined_at: data.joinedAt || Date.now()
            };
            await storageService.saveGroupMember(member);
            
            const existingIndex = updatedMembers.findIndex(m => m.member_pub === key);
            const memberWithStatus = { ...member, isOnline: true }; // Assume online on update
            if (existingIndex > -1) {
                updatedMembers[existingIndex] = memberWithStatus;
                console.log(`[Member Listener] Member ${key} updated. Current members:`, updatedMembers);
            } else {
                updatedMembers.push(memberWithStatus);
                console.log(`[Member Listener] Member ${key} joined. Current members:`, updatedMembers);
            }
        }
        membersByGroup.value[groupPub] = updatedMembers; // Assign the new array to trigger reactivity
        console.log(`[Member Listener] Final membersByGroup[${groupPub}]:`, membersByGroup.value[groupPub]);
    });
  };

  const listenToGroupMessages = (groupPub: string) => {
    if (messageListeners[groupPub]) messageListeners[groupPub].off();

    messageListeners[groupPub] = gun.get(`group_${groupPub}`).map().on(async (data, id) => {
        if (!data || !id) return;
        
        lastMessageReceivedTime = Date.now(); // Update last message received time
        
        // Check if message already exists locally (optimistic update or already processed)
        let existingIndex = messagesByGroup.value[groupPub]?.findIndex(m => m.msg_id === id);

        if (existingIndex > -1) {
            const existingMessage = messagesByGroup.value[groupPub][existingIndex];
            if (data.sender_pub === safeUserPub.value) {
                // This is our own message echoing back from Gun
                if (existingMessage.status !== 'sent') {
                    await storageService.updateGroupMessageStatus(existingMessage.msg_id, 'sent');
                    existingMessage.status = 'sent'; // Direct mutation is enough for reactivity here
                }
            }
            return; // Message already present, prevent duplicate addition
        }

        // If we reach here, it's a new message (either from another user, or our own that wasn't optimistically added initially)
        const message = {
            msg_id: id,
            group_pub: groupPub,
            sender_pub: data.sender_pub,
            sender_alias: data.sender_alias,
            content: data.content,
            content_type: data.content_type || 'text',
            timestamp: data.timestamp,
            status: 'sent' // Newly received messages are always 'sent'
        };

        const dbMessage = await storageService.insertGroupMessage(message);

        // Update group preview for received messages
        if (dbMessage.content_type !== 'leave_notification') {
            await storageService.updateGroupPreview(groupPub, dbMessage.content, dbMessage.timestamp);
            
            // Update local preview state
            groupPreviews.value[groupPub] = {
                last_msg: dbMessage.content.length > 20 ? dbMessage.content.slice(0, 20) + '…' : dbMessage.content,
                last_time: dbMessage.timestamp
            };
        }

        // Only push to messagesByGroup if the group is currently selected
        if (groupPub === currentGroup.value) {
            if (!messagesByGroup.value[groupPub]) {
                messagesByGroup.value[groupPub] = [];
            }
            messagesByGroup.value[groupPub].push({ ...dbMessage, formattedTime: formatMessageTime(dbMessage.timestamp) });
        }

        if (dbMessage.content_type === 'leave_notification') {
            await storageService.deleteGroupMember(groupPub, dbMessage.sender_pub);
            if (membersByGroup.value[groupPub]) {
                membersByGroup.value[groupPub] = membersByGroup.value[groupPub].filter(m => m.member_pub !== dbMessage.sender_pub);
            }
        }
    });
  };

  const selectGroup = async (pub: string) => {
    currentGroup.value = pub;
    const group = groups.value.find(g => g.pub === pub);
    currentGroupName.value = group ? group.name : '';

    const initialMessages = await storageService.getGroupMessages(pub, 20);
    const formattedMessages = initialMessages.map(m => ({ ...m, formattedTime: formatMessageTime(m.timestamp) })).reverse();
    if (!messagesByGroup.value[pub]) {
      messagesByGroup.value[pub] = [];
    }
    messagesByGroup.value[pub].splice(0, messagesByGroup.value[pub].length, ...formattedMessages);

    if (initialMessages.length > 0) {
        oldestMessageId.value[pub] = initialMessages[initialMessages.length - 1]?.id;
    }

    // Fetch and set initial members snapshot from GunDB
    const initialMembers = await fetchCurrentGroupMembersSnapshot(pub);
    membersByGroup.value[pub] = initialMembers;
    console.log(`[selectGroup] Initial members loaded for ${pub}:`, membersByGroup.value[pub]);

    // Fetch and set initial votes snapshot from GunDB
    const initialVotes = await fetchCurrentGroupVotesSnapshot(pub);
    votesByGroup.value[pub] = initialVotes;
    console.log(`[selectGroup] Initial votes loaded for ${pub}:`, votesByGroup.value[pub]);

    // Mark group as read when selected
    markGroupAsRead(pub);
  };

  // New helper to fetch current members snapshot from GunDB
  const fetchCurrentGroupMembersSnapshot = async (groupPub: string): Promise<GroupChatMember[]> => {
    return new Promise(resolve => {
      const tempMembers: GroupChatMember[] = [];
      gun.get(`group_${groupPub}_members`).map().once((data, key) => {
        if (data) {
          tempMembers.push({ member_pub: key, alias: data.alias, joined_at: data.joinedAt || Date.now(), isOnline: false });
        }
      });
      // Resolve after a short delay to ensure all initial `once` data has been processed
      setTimeout(() => {
        console.log(`[fetchCurrentGroupMembersSnapshot] Resolved for ${groupPub}. Members:`, tempMembers);
        resolve(tempMembers);
      }, 100); // Small delay, adjust if needed
    });
  };

  // New helper to fetch current votes snapshot from GunDB
  const fetchCurrentGroupVotesSnapshot = async (groupPub: string): Promise<GroupChatVote[]> => {
    return new Promise(resolve => {
      const tempVotes: GroupChatVote[] = [];
      gun.get(`group_${groupPub}_clear_votes`).map().once((data, key) => {
        if (data && (data as any).agreed) {
          tempVotes.push({ voterId: key, vote: 'agree', timestamp: (data as any).timestamp || Date.now() });
        }
      });
      // Resolve after a short delay to ensure all initial `once` data has been processed
      setTimeout(() => {
        console.log(`[fetchCurrentGroupVotesSnapshot] Resolved for ${groupPub}. Votes:`, tempVotes);
        resolve(tempVotes);
      }, 100); // Small delay, adjust if needed
    });
  };

  const setCurrentGroup = (pub: string | null) => {
    currentGroup.value = pub;
    if (pub) {
        const group = groups.value.find((g) => g.pub === pub);
        currentGroupName.value = group ? group.name : '';
    } else {
        currentGroupName.value = '';
    }
  };
  
  const getCurrentGroup = (): string | null => {
    return currentGroup.value;
  };
  
  const loadMoreMessages = async (groupPub: string) => {
    if (!oldestMessageId.value[groupPub]) return;
    
    const moreMessages = await storageService.getGroupMessages(groupPub, 10, oldestMessageId.value[groupPub]);

    if (moreMessages.length > 0) {
        oldestMessageId.value[groupPub] = moreMessages[moreMessages.length - 1]?.id;
        const formattedMessages = moreMessages.map(m => ({ ...m, formattedTime: formatMessageTime(m.timestamp) })).reverse();
        messagesByGroup.value[groupPub].splice(0, 0, ...formattedMessages);
    } else {
        console.log("No more messages to load.");
        oldestMessageId.value[groupPub] = undefined; // No more older messages
    }
  };

  const sendMessage = async (content: string) => {
    if (!content.trim() || !currentGroup.value) return;

    const groupPub = currentGroup.value;
    const message = {
        msg_id: uuidv4(),
        group_pub: groupPub,
        sender_pub: safeUserPub.value,
        sender_alias: safeUserAlias.value,
        content: content.trim(),
        content_type: 'text',
        timestamp: Date.now(),
        status: 'pending'
    };
    
    const dbMessage = await storageService.insertGroupMessage(message);
    messagesByGroup.value[groupPub]?.push({ ...dbMessage, formattedTime: formatMessageTime(dbMessage.timestamp) });

    // Update group preview
    await storageService.updateGroupPreview(groupPub, content.trim(), message.timestamp);
    
    // Update local preview state
    groupPreviews.value[groupPub] = {
        last_msg: content.trim().length > 20 ? content.trim().slice(0, 20) + '…' : content.trim(),
        last_time: message.timestamp
    };

    // Mark as read immediately after sending our own message
    markGroupAsRead(groupPub);

    const gunMessage = {
        ...message,
        id: message.msg_id // Gun uses `id` for its own reference
    };
    
    gun.get(`group_${groupPub}`).get(message.msg_id).put(gunMessage, async (ack: any) => {
        if (ack.err) {
            console.error('Gun send error:', ack.err);
            await storageService.updateGroupMessageStatus(message.msg_id, 'failed');
            const msgIndex = messagesByGroup.value[groupPub]?.findIndex(m => m.msg_id === message.msg_id);
            if (msgIndex > -1) {
                messagesByGroup.value[groupPub][msgIndex].status = 'failed';
            }
        } else {
            await storageService.updateGroupMessageStatus(message.msg_id, 'sent');
            const msgIndex = messagesByGroup.value[groupPub]?.findIndex(m => m.msg_id === message.msg_id);
            if (msgIndex > -1) {
                messagesByGroup.value[groupPub][msgIndex].status = 'sent';
            }
        }
    });
  };

  const listenToGroupNameChanges = (groupPub: string) => {
    if (groupNameListeners[groupPub]) {
      groupNameListeners[groupPub].off();
    }
    groupNameListeners[groupPub] = gun.get(`group_name_${groupPub}`).on(async (data) => {
      if (data && data.name) {
        const group = groups.value.find(g => g.pub === groupPub);
        if (group && group.name !== data.name) {
          group.name = data.name;
          if (currentGroup.value === groupPub) {
            currentGroupName.value = data.name;
          }
          await storageService.saveGroup({
            group_pub: group.pub,
            name: group.name,
            key_pair_json: JSON.stringify(group.pair)
          });
        }
      }
    });
  };

  const saveGroupNameToNetwork = async (groupPub: string, name: string) => {
    await gun.get(`group_name_${groupPub}`).put({ name });
  };

  const copyKeyPair = async (pair: GroupChatKeyPair) => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(pair, null, 2));
      const toast = await toastController.create({
        message: 'Copied',
        duration: 2000,
        position: 'top',
      });
      await toast.present();
    } catch (error) {
      // Handle error silently
    }
  };

  const voteToClear = async () => {
    if (!currentGroup.value || !safeUserPub.value) return;
    await gun.get(`group_${currentGroup.value}_clear_votes`).get(safeUserPub.value).put({ agreed: true });
  };

  const cancelVote = async () => {
      if (!currentGroup.value || !safeUserPub.value) return;
      await gun.get(`group_${currentGroup.value}_clear_votes`).get(safeUserPub.value).put({ agreed: false });
  };

  // Vote-to-clear logic remains, but execution changes
  const initiateClearChat = async (groupPub: string) => {
      // Assuming canClearChat logic is still valid
      // 1. Clear local DB first
      await storageService.clearGroupMessages(groupPub);
      messagesByGroup.value[groupPub] = [];
      
      // 2. Clear network data
      gun.get(`group_${groupPub}`).put(null);
      
      // 3. Broadcast a clear signal (optional, for robustness)
      gun.get(`group_${groupPub}_clear_signal`).put({ initiator: safeUserPub.value, timestamp: Date.now() });

      const toast = await toastController.create({
        message: 'Chat history cleared',
        duration: 2000,
        position: 'top',
      });
      await toast.present();
  };

  const listenToClearVotes = (groupPub: string) => {
    if (voteListeners[groupPub]) voteListeners[groupPub].off();
    voteListeners[groupPub] = gun.get(`group_${groupPub}_clear_votes`).map().on((data, key) => {
        console.log(`[Vote Listener] Group: ${groupPub}, Key: ${key}, Data:`, data);
        if (!votesByGroup.value[groupPub]) {
            votesByGroup.value[groupPub] = [];
        }
        const existingIndex = votesByGroup.value[groupPub].findIndex(v => v.voterId === key);
        
        let updatedVotes = [...votesByGroup.value[groupPub]]; // Create a new array for reactivity

        if (!data) {
            // Vote removed (unvote)
            if (existingIndex > -1) {
                updatedVotes.splice(existingIndex, 1);
                console.log(`[Vote Listener] Vote removed for ${key}. Current votes:`, updatedVotes);
            }
        } else {
            // Vote added or updated
            const vote: GroupChatVote = {
                voterId: key,
                vote: (data as any).agreed ? 'agree' : 'disagree',
                timestamp: Date.now(), // Use local time for timestamp or data.timestamp if available from Gun
            };
            if (existingIndex > -1) {
                updatedVotes[existingIndex] = vote;
                console.log(`[Vote Listener] Vote updated for ${key}. Current votes:`, updatedVotes);
            } else {
                updatedVotes.push(vote);
                console.log(`[Vote Listener] Vote added for ${key}. Current votes:`, updatedVotes);
            }
        }
        votesByGroup.value[groupPub] = updatedVotes; // Assign the new array to trigger reactivity
        console.log(`[Vote Listener] Final votesByGroup[${groupPub}]:`, votesByGroup.value[groupPub]);
    });
  };

  onUnmounted(() => {
    Object.values(messageListeners).forEach((listener) => listener && listener.off());
    Object.values(memberListeners).forEach((listener) => listener && listener.off());
    Object.values(voteListeners).forEach((listener) => listener && listener.off());
    Object.values(groupNameListeners).forEach((listener) => listener && listener.off());
    if (messageActivityTimer) {
        clearInterval(messageActivityTimer);
    }
  });

  const startMessageSyncMonitor = (groupPub: string) => {
    if (messageActivityTimer) {
        clearInterval(messageActivityTimer);
    }

    lastMessageReceivedTime = Date.now(); // Initialize with current time

    messageActivityTimer = setInterval(async () => {
        const now = Date.now();
        if (now - lastMessageReceivedTime > 2000) { // No new messages for 2 seconds
            clearInterval(messageActivityTimer as NodeJS.Timeout);
            messageActivityTimer = null;
            isInitialGroupMessageSyncing.value = false;
           // console.log(`Initial sync finished for ${groupPub}. Loading last 20 messages.`);
            
            // Load the last 20 messages from SQLite for the selected group
            const initialMessages = await storageService.getGroupMessages(groupPub, 20);
            const formattedMessages = initialMessages.map(m => ({ ...m, formattedTime: formatMessageTime(m.timestamp) })).reverse();
            if (!messagesByGroup.value[groupPub]) {
              messagesByGroup.value[groupPub] = [];
            }
            // Merge new messages without duplicates
            const existingMessageIds = new Set(messagesByGroup.value[groupPub].map(msg => msg.msg_id));
            const newMessagesToDisplay = formattedMessages.filter(msg => !existingMessageIds.has(msg.msg_id));
            messagesByGroup.value[groupPub].splice(0, 0, ...newMessagesToDisplay); // Add to the beginning

            if (initialMessages.length > 0) {
                oldestMessageId.value[groupPub] = initialMessages[initialMessages.length - 1]?.id;
            }
        }
    }, 500); // Check every 500ms
};
  
  // Return everything needed by the UI
  return {
    newGroupName,
    joinGroupKey,
    groups,
    groupPreviews,
    currentGroup,
    currentGroupName,
    messagesByGroup,
    membersByGroup,
    tempKeyPair,
    groupSessions,
    safeUserAlias,
    safeUserPub,
    loadGroups: loadGroupsFromDB,
    createGroup,
    joinGroup,
    joinGroupWithKeyPair,
    deleteGroup,
    setCurrentGroup,
    getCurrentGroup,
    selectGroup,
    loadMoreMessages,
    sendMessage,
    copyKeyPair,
    initiateClearChat, // This now takes groupPub
    agreeCount,
    canClearChat,
    voteToClear,
    cancelVote,
    getAliasRealtime,
    triggerLightHaptic,
    markGroupAsRead,
    isInitialGroupMessageSyncing,
    // ... any other functions needed, like voteToClear
  };
}; 

/* ------------------------------------------------------------------
   Debugging Logs for message rendering issues
------------------------------------------------------------------*/
// No longer needed after fixing. Will be removed in final cleanup. 
// This is here to prevent accidental deletion during automated edits. 
// Remove this entire section in the final version. 